import React from 'react';

/**
 * StatusBadge — the single, canonical status indicator for the whole app.
 *
 * Enterprise pattern (Linear / Vercel / Stripe / GitHub Projects): a neutral
 * chip with one small colour-carrying dot. Colour is a *signal* on the dot, not
 * a tint flooding the whole label — so a column of statuses reads calm, never a
 * rainbow. No uppercase, no monospace, no coloured borders (the old "dashboard"
 * tells). The dot pulses only for live/in-progress states.
 *
 * <StatusBadge tone="success" label="Completed" />
 * <StatusBadge tone="warning" label="Running" pulse />
 * <StatusBadge tone="success" label="Passed" count={12} />   // summary metric
 *
 * tone — success | warning | danger | info | neutral
 * count — optional trailing number; the label stays neutral, only the dot
 *         carries colour, so a header row of counts reads calm, not a rainbow.
 */
const TONE = {
  success: 'var(--success)',
  warning: 'var(--warning)',
  danger:  'var(--threat-critical)',
  info:    'var(--accent)',
  neutral: 'var(--text-muted)',
};

export default function StatusBadge({
  tone = 'neutral',
  label,
  count,
  pulse = false,
  size = 'md',
  style,
  ...rest
}) {
  const dot = TONE[tone] || TONE.neutral;
  const sm = size === 'sm';
  return (
    <span
      className="status-badge"
      style={{
        display: 'inline-flex', alignItems: 'center',
        gap: sm ? 6 : 7,
        padding: sm ? '2px 8px' : '3px 10px',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-subtle)',
        color: 'var(--text-secondary)',
        fontSize: sm ? 11 : 'var(--fs-xs)',
        fontWeight: 'var(--fw-med)',
        lineHeight: 1.45,
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        ...style,
      }}
      {...rest}
    >
      <span
        aria-hidden
        style={{
          width: 6, height: 6, borderRadius: '50%',
          background: dot, flexShrink: 0,
          animation: pulse ? 'status-pulse 1.6s ease-in-out infinite' : 'none',
        }}
      />
      {label}
      {count != null && (
        <span style={{
          fontWeight: 'var(--fw-bold)',
          color: 'var(--text-primary)',
          fontVariantNumeric: 'tabular-nums',
          marginLeft: 1,
        }}>
          {count}
        </span>
      )}
    </span>
  );
}
